import { RefObject, useCallback, useEffect, useState } from "react";

const useVisibility = (ref: RefObject<HTMLElement | null>) => {
  const [isVisible, setIsVisible] = useState(false);

  const checkVisibility = useCallback(() => {
    if (!ref.current) return;

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        entries => {
          setIsVisible(entries[0].isIntersecting);
        },
        { threshold: 0.1 },
      );

      observer.observe(ref.current);

      return () => observer.disconnect();
    } else {
      // Fallback for browsers that don't support IntersectionObserver
      const isElementInViewport = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        );
      };

      const checkVisibilityFallback = () => {
        if (ref.current) {
          setIsVisible(isElementInViewport(ref.current));
        }
      };

      checkVisibilityFallback();
      (window as Window).addEventListener("scroll", checkVisibilityFallback);
      (window as Window).addEventListener("resize", checkVisibilityFallback);

      return () => {
        window.removeEventListener("scroll", checkVisibilityFallback);
        window.removeEventListener("resize", checkVisibilityFallback);
      };
    }
  }, [ref]);

  useEffect(() => {
    const cleanup = checkVisibility();
    return cleanup;
  }, [checkVisibility]);

  return isVisible;
};

export default useVisibility;
